import { useState, useEffect, useCallback, useRef } from 'react';
import Request from 'superagent';
import { NAMEPSACE, IS_ADMIN } from "../components/Globals";
import { defaultFilters, rangeChanged } from '../components/filter/filterOptions';

const SEARCH_PAGE_SIZE = 50;

export const useMovieData = () => {
    // List that contains all imdb_ids of films shown to user
    const [watched, setWatched] = useState([]);

    // Movie state
    const [movieState, setMovieState] = useState({
        movieLoading: true,
        movieFound: false,
        movieNotFound: false,
    });

    // Movie data
    const [movieData, setMovieData] = useState({
        imdb_id: null,
        title: null,
        language: null,
        year: null,
        runtime: null,
        writer: null,
        director: null,
        trailer: null,
        api_data: null,
        ratings: { rottentomatoes: null, imdb: null },
        streams: {
            youtube: { url: null, price: null },
            itunes: { url: null, price: null },
            googleplay: { url: null, price: null },
            amazon: { url: null, price: null }
        },
    });

    // Filters state
    const [filters, setFilters] = useState(defaultFilters);

    // Free-text search: results are fetched a page at a time and played in order
    const [searchQuery, setSearchQuery] = useState('');
    const [searchIntent, setSearchIntent] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchQueueRef = useRef([]);

    // State setters
    const setMovieLoading = useCallback(() => {
        setMovieState({
            movieLoading: true,
            movieFound: false,
            movieNotFound: false,
        });
    }, []);

    const setMovieFound = useCallback(() => {
        setMovieState({
            movieLoading: false,
            movieFound: true,
            movieNotFound: false,
        });
    }, []);

    const setMovieNotFound = useCallback(() => {
        setMovieState({
            movieLoading: false,
            movieFound: false,
            movieNotFound: true,
        });
    }, []);

    // URL parameter generation.
    // For searches the release range is only sent if the user changed it, so the
    // query itself ("a 90s comedy") decides the era and defaults don't hide older films.
    const getUrlParameters = useCallback(({ forSearch = false } = {}) => {
        var rotten_min = 'rotten_min=' + filters.rottentomatoes.min;
        var rotten_max = 'rotten_max=' + filters.rottentomatoes.max;
        var imdb_max = 'imdb_min=' + filters.imdb.min;
        var imdb_min = 'imdb_max=' + filters.imdb.max;
        var runtime_min = 'runtime_min=' + filters.runtime.min;
        var runtime_max = 'runtime_max=' + filters.runtime.max;
        var from_year = 'from_year=' + filters.released.min;
        var to_year = 'to_year=' + filters.released.max;

        var params = [rotten_min, rotten_max, imdb_max, imdb_min, runtime_min, runtime_max];
        if (!forSearch || rangeChanged(filters, 'released')) {
            params.push(to_year, from_year);
        }
        var url_params = params.join('&')

        var languages = [];
        for (var i = 0; i < filters.languages.length; i++) {
            if (filters.languages[i]['checked'] == true) {
                languages.push(filters.languages[i]['value'])
            }
        }
        if (languages.length > 0) {
            url_params = url_params + '&language=' + languages.join(',')
        }

        var streams = [];
        for (var i = 0; i < filters.streams.length; i++) {
            if (filters.streams[i]['checked'] == true) {
                streams.push(filters.streams[i]['value'])
            }
        }
        if (streams.length > 0) {
            url_params = url_params + '&streams=' + streams.join(',')
        }

        var genres = [];
        for (var i = 0; i < filters.genres.length; i++) {
            if (filters.genres[i]['checked'] == true) {
                genres.push(filters.genres[i]['value'])
            }
        }
        if (genres.length > 0) {
            url_params += (url_params ? '&' : '') +
                genres.map(g => `genres=${encodeURIComponent(g)}`).join('&');
        }

        if (watched.length > 0) {
            url_params = url_params + '&seen=' + watched.join(',')
        }

        // /opensesame only plays films we haven't rated yet
        if (IS_ADMIN) {
            url_params = url_params + '&unrated=true'
        } else {
            url_params = url_params + '&unrated=false'
        }

        return url_params;
    }, [filters, watched]);

    // Movie data setters
    const updateMovieData = useCallback((movie) => {
        setMovieData({
            imdb_id: movie.imdb_id,
            title: movie.title,
            language: movie.orig_language,
            released: movie.released.substring(0, 4),
            runtime: movie.runtime,
            writer: movie.writer,
            director: movie.director,
            trailer: movie.trailer,
            ratings: { imdb: movie.imdb_rating, rottentomatoes: movie.rotten_tomatoes_rating, kino_rating: movie.kino_rating },
            streams: {},
        });
    }, []);

    const updateCurrentMovieKinoRating = useCallback((kinoRating) => {
        const parsed = Number(kinoRating);
        if (Number.isNaN(parsed)) return;
        setMovieData(prev => ({
            ...prev,
            ratings: {
                ...(prev.ratings || {}),
                kino_rating: parsed,
            },
        }));
    }, []);

    // Watched list management
    const addToWatched = useCallback((imdb_id) => {
        setWatched(prev => {
            const filtered = prev.filter(e => e !== imdb_id);
            return [...filtered, imdb_id];
        });
    }, []);

    const clearWatched = useCallback(() => {
        setWatched([]);
        // Filters changed, so any queued search results are stale
        searchQueueRef.current = [];
    }, []);

    // API calls
    const playMovie = useCallback((movie) => {
        addToWatched(movie.imdb_id);
        updateMovieData(movie);
        setMovieFound();
    }, [addToWatched, updateMovieData, setMovieFound]);

    const loadSearchPage = useCallback((query) => {
        setMovieLoading();
        setSearchLoading(true);
        const url = NAMEPSACE + "movies/search?q=" + encodeURIComponent(query) + "&" +
            getUrlParameters({ forSearch: true }) + "&limit=" + SEARCH_PAGE_SIZE;
        return Request.get(url).then((response) => {
            const data = JSON.parse(response["text"]);
            setSearchIntent(data.intent);
            const [first, ...rest] = data.results;
            searchQueueRef.current = rest;
            if (first) {
                playMovie(first);
            } else {
                setMovieNotFound();
            }
        }).catch(() => setMovieNotFound())
          .finally(() => setSearchLoading(false));
    }, [getUrlParameters, setMovieLoading, playMovie, setMovieNotFound]);

    const runSearch = useCallback((query) => {
        const q = query.trim();
        if (q.length < 2) return;
        setSearchQuery(q);
        setSearchIntent(null);
        searchQueueRef.current = [];
        return loadSearchPage(q);
    }, [loadSearchPage]);

    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setSearchIntent(null);
        searchQueueRef.current = [];
    }, []);

    const loadRandomMovie = useCallback(() => {
        var url_params = getUrlParameters();
        setMovieLoading();
        var url = NAMEPSACE + "movies/random?" + url_params;

        return Request.get(url).then((response) => {
            var loaded_response = JSON.parse(response["text"]);
            if (loaded_response.length > 0) {
                var movie_data = loaded_response[0];
                addToWatched(movie_data.imdb_id);
                updateMovieData(movie_data);
                setMovieFound();
            } else {
                setMovieNotFound();
            }
        }).catch(() => setMovieNotFound());
    }, [getUrlParameters, setMovieLoading, updateMovieData, setMovieFound, setMovieNotFound, addToWatched]);

    const getNextMovie = useCallback(() => {
        if (searchQuery) {
            const next = searchQueueRef.current.shift();
            if (next) {
                playMovie(next);
            } else {
                loadSearchPage(searchQuery);
            }
            return;
        }
        loadRandomMovie();
    }, [searchQuery, playMovie, loadSearchPage, loadRandomMovie]);

    // "Show films" in the search overlay: the description (if any) plus every filter.
    // Resolves once the first film is ready, so the overlay can wait for it.
    const showFilms = useCallback((query) => {
        const q = (query || '').trim();
        if (q.length >= 2) {
            return runSearch(q);
        }
        clearSearch();
        return loadRandomMovie();
    }, [runSearch, clearSearch, loadRandomMovie]);

    const getSpecificMovie = useCallback((imdb_id) => {
        if (!imdb_id) return;
        setMovieLoading();
        const url = NAMEPSACE + `movies?imdb_id=${encodeURIComponent(imdb_id)}`;
        Request.get(url).then((response) => {
            const movie = JSON.parse(response["text"]);
            if (movie && movie.imdb_id) {
                addToWatched(movie.imdb_id);
                updateMovieData(movie);
                setMovieFound();
            } else {
                setMovieNotFound();
            }
        }).catch(() => setMovieNotFound());
    }, [setMovieLoading, updateMovieData, setMovieFound, setMovieNotFound, addToWatched]);

    // Filter functions
    const toggleAllCheckboxes = useCallback((filter_id) => {
        clearWatched();
        const all_checked = allFiltersChecked(filter_id);
        setFilters(prev => ({
            ...prev,
            [filter_id]: prev[filter_id].map(item => ({
                ...item,
                checked: !all_checked
            }))
        }));
    }, [clearWatched]);

    const toggleCheckbox = useCallback((filter_id, value) => {
        clearWatched();
        setFilters(prev => ({
            ...prev,
            [filter_id]: prev[filter_id].map(item => ({
                ...item,
                checked: item.value === value ? !item.checked : item.checked
            }))
        }));
    }, [clearWatched]);

    const allFiltersChecked = useCallback((filter_id) => {
        return filters[filter_id].every(item => item.checked);
    }, [filters]);

    const updateRange = useCallback((range_id, min, max) => {
        clearWatched();
        setFilters(prev => ({
            ...prev,
            [range_id]: { min, max }
        }));
    }, [clearWatched]);

    const resetFilters = useCallback(() => {
        clearWatched();
        setFilters(defaultFilters());
    }, [clearWatched]);

    // Status line × : drop the description and every filter
    const clearAll = useCallback(() => {
        clearSearch();
        resetFilters();
    }, [clearSearch, resetFilters]);

    // Initialize on mount
    useEffect(() => {
        getNextMovie();
    }, []); // Only run once on mount

    return {
        // State
        movieState,
        movieData,
        filters,
        watched,
        searchQuery,
        searchIntent,
        searchLoading,
        
        // Actions
        getNextMovie,
        runSearch,
        clearSearch,
        showFilms,
        clearAll,
        getSpecificMovie,
        updateCurrentMovieKinoRating,
        clearWatched,
        
        // Filter actions
        toggleAllCheckboxes,
        toggleCheckbox,
        allFiltersChecked,
        updateRange,
        resetFilters,
        
        // State setters (if needed externally)
        setMovieLoading,
        setMovieFound,
        setMovieNotFound,
    };
};
